import React, {useRef, useEffect, useState} from "react";
import {cn} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

export interface DashboardDropdownItem {
  value: string;
  label: string;
  disabled?: boolean;
  disabledReason?: string;
  rightIconClassName?: string;
  rightIconTooltip?: string;
}

export interface DashboardDropdownGroup {
  label?: string;
  items: DashboardDropdownItem[];
}

interface Props {
  groups: DashboardDropdownGroup[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  label: string;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  containerClassName?: string;
  buttonClassName?: string;
  menuClassName?: string;
  legendSize?: 'normal' | 'large';
}

const DashboardDropdown: React.FC<Props> = (
  {
    groups,
    selectedValues,
    onChange,
    label,
    multiple = false,
    disabled,
    placeholder = "Alle",
    required,
    containerClassName,
    buttonClassName,
    menuClassName,
    legendSize = 'normal',
  }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const allItems = groups.flatMap((g) => g.items);

  const selectedItems = allItems.filter((item) =>
    selectedValues.includes(item.value)
  );

  let buttonLabel = placeholder;

  if (selectedItems.length === 1) {
    buttonLabel = selectedItems[0].label;
  } else if (selectedItems.length > 1) {
    buttonLabel = selectedItems.map((i) => i.label).join(", ");
  }

  const toggleValue = (value: string) => {
    const item = allItems.find((i) => i.value === value);
    if (item?.disabled) return;
    if (!multiple) {
      onChange([value]);
      setOpen(false);
      return;
    }
    const isSelected = selectedValues.includes(value);
    if (value === "ALL") {
      onChange(["ALL"]);
      return;
    }
    let next = selectedValues.filter((v) => v !== "ALL");
    if (isSelected) {
      next = next.filter((v) => v !== value);
    } else {
      next = [...next, value];
    }
    onChange(next);
  };

  const legendTextSize = legendSize === "large" ? "text-xl tablet:text-2xl" : "text-lg tablet:text-xl";

  return (
    <div ref={ref} className={cn("relative flex flex-col gap-2 w-full", containerClassName)}>
      <legend className={cn("font-semibold px-1 w-full", legendTextSize)}>
        {label} {required && <span className="text-red-secondary">*</span>}
      </legend>

      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((o) => !o);
        }}
        className={cn(
          "flex items-center justify-between gap-3 px-6 py-3 text-base tablet:text-lg bg-white rounded-xl w-full text-black-subtile border-2",
          open
            ? "rounded-b-none border-2 border-b-0 border-brown-secondary"
            : "border-brown-primary",
          disabled && "opacity-60 cursor-not-allowed",
          buttonClassName,
        )}
      >
        <span className="truncate">{buttonLabel}</span>
        <span className={cn("!text-3xl !leading-none", open ? "icon-chevron-up" : "icon-chevron-down")}/>
      </button>

      {open && (
        <ul
          className={cn(
            "absolute z-10 overflow-y-auto bg-white border-2 border-t-0 shadow border-brown-secondary rounded-b-xl w-full min-h-fit top-full",
            menuClassName,
          )}
          role="listbox"
        >
          {groups.map((group, gi) => (
            <li
              key={gi}
              className={cn(gi > 0 && "dropdown-group-separator")}
            >
              {group.label && (
                <div className="px-4 pt-3 pb-1 text-sm font-semibold text-black-subtile">
                  {group.label}
                </div>
              )}

              <ul>
                {group.items.map((item) => {
                  const isSelected = selectedValues.includes(item.value);
                  const isDisabled = !!item.disabled;

                  return (
                    <li
                      key={item.value}
                      onClick={() => {
                        if (isDisabled) return;
                        toggleValue(item.value);
                      }}
                      aria-disabled={isDisabled}
                      title={isDisabled ? item.disabledReason ?? "Ikke tilgjengelig" : undefined}
                      className={cn(
                        "dropdown-checkbox px-4 py-2 flex items-center gap-3",
                        isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-yellow-25 cursor-pointer",
                      )}
                    >
                      {multiple && (
                        <div
                          className={cn("dropdown-checkbox-box", isSelected && "dropdown-checkbox-checked")}
                        />
                      )}
                      <span className="truncate">{item.label}</span>
                      {item.rightIconClassName && (
                        <span
                          className={cn(item.rightIconClassName, "!text-sm !leading-none ml-auto text-black-subtile")}
                          aria-hidden="true" title={item.rightIconTooltip ?? undefined}/>
                      )}
                      {!multiple && isSelected && (
                        <div className={cn("icon-check", item.rightIconClassName ? "ml-2" : "ml-auto")}/>
                      )}
                      {multiple && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          disabled={isDisabled}
                          className="sr-only"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DashboardDropdown;
